import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  PlayArrow,
  Refresh,
  TrendingUp,
  Storage,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface DailyTask {
  scheduler_name: string;
  enabled: boolean;
  schedule: string;
  next_run: string;
  last_run: {
    timestamp?: string;
    status?: string;
    total_stocks?: number;
    successful?: number;
    successful_fetches?: number;
    failed?: number;
    failed_fetches?: number;
    buy_opportunities?: number;
    sell_opportunities?: number;
    db_inserted?: number;
    db_updated?: number;
  } | null;
  description: string;
}

interface DailyTasksResponse {
  tasks: DailyTask[];
  total_tasks: number;
  active_tasks: number;
  timestamp: string;
}

interface CollectionHistory {
  snapshot_date: string;
  total_snapshots: number;
  successful: number;
  failed: number;
  avg_fundamental_score: number | null;
}

interface BackfillStatus {
  status: string;
  current_batch: number;
  total_batches: number;
  batches_remaining: number;
  stocks_completed: number;
  total_target_stocks: number;
  stocks_progress_percent: number;
  snapshots_completed: number;
  total_target_snapshots: number;
  snapshots_progress_percent: number;
  estimated_minutes_remaining: number;
  estimated_hours_remaining: number;
  database_stats: {
    total_stocks: number;
    fully_backfilled_stocks: number;
    total_snapshots: number;
    earliest_date: string | null;
    latest_date: string | null;
  };
}

export default function DailyTasks() {
  const theme = useTheme();
  const [tasks, setTasks] = useState<DailyTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggeringCollection, setTriggeringCollection] = useState(false);
  const [collectionHistory, setCollectionHistory] = useState<CollectionHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState<BackfillStatus | null>(null);
  const [showBackfillDetails, setShowBackfillDetails] = useState(false);

  const fetchTasksStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/v1/daily-tasks/status');
      const data: DailyTasksResponse = await response.json();
      setTasks(data.tasks);
    } catch (err: any) {
      setError(err.message || 'Failed to load daily tasks status');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionHistory = async () => {
    try {
      const response = await fetch('/api/v1/daily-tasks/fundamental-collection/history?limit=30');
      const data = await response.json();
      setCollectionHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching collection history:', err);
    }
  };

  const fetchBackfillStatus = async () => {
    try {
      const response = await fetch('/api/v1/daily-tasks/backfill/status');
      const data = await response.json();
      if (data.status === 'success') {
        setBackfillStatus(data.backfill);
      }
    } catch (err) {
      console.error('Error fetching backfill status:', err);
    }
  };

  const triggerFundamentalCollection = async () => {
    try {
      setTriggeringCollection(true);
      const response = await fetch('/api/v1/daily-tasks/fundamental-collection/run', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to trigger collection');
      }

      alert('Fundamental collection completed successfully! Check history for results.');
      await fetchTasksStatus();
      await fetchCollectionHistory();
    } catch (err: any) {
      alert(err.message || 'Failed to trigger fundamental collection');
      console.error('Error triggering collection:', err);
    } finally {
      setTriggeringCollection(false);
    }
  };

  useEffect(() => {
    fetchTasksStatus();
    fetchCollectionHistory();
    fetchBackfillStatus();

    // Refresh every 60 seconds
    const interval = setInterval(() => {
      fetchTasksStatus();
      fetchBackfillStatus();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatTimeUntil = (nextRunIso: string) => {
    const now = new Date();
    const nextRun = new Date(nextRunIso);
    const diffMs = nextRun.getTime() - now.getTime();

    if (diffMs < 0) {
      return 'Running soon...';
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `in ${days}d ${hours % 24}h`;
    }

    return `in ${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Daily Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Automated jobs that run daily after market close
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchTasksStatus}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Tasks
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {tasks.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Active Tasks
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {tasks.filter(t => t.enabled).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Next Run
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {tasks.length > 0 && formatTimeUntil(tasks[0].next_run)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Historical Backfill Progress */}
      {backfillStatus && backfillStatus.status === 'running' && (
        <Card sx={{ mb: 3, borderLeft: `4px solid ${theme.palette.info.main}` }}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={2}>
                <Storage />
                <Typography variant="h6">Historical Fundamental Data Backfill</Typography>
                <Chip
                  label="In Progress"
                  color="info"
                  size="small"
                  icon={<CircularProgress size={12} sx={{ color: 'inherit' }} />}
                />
              </Box>
            }
            subheader={`Collecting 5 years of quarterly fundamental data for ${backfillStatus.total_target_stocks.toLocaleString()} stocks`}
            action={
              <IconButton onClick={() => setShowBackfillDetails(!showBackfillDetails)}>
                {showBackfillDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            }
          />

          <CardContent>
            <Grid container spacing={3}>
              {/* Progress Overview */}
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Batches Completed
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {backfillStatus.current_batch} / {backfillStatus.total_batches}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: theme.palette.grey[300],
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${(backfillStatus.current_batch / backfillStatus.total_batches) * 100}%`,
                          bgcolor: theme.palette.info.main,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {((backfillStatus.current_batch / backfillStatus.total_batches) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Stocks Completed
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {backfillStatus.database_stats.fully_backfilled_stocks.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {backfillStatus.stocks_progress_percent.toFixed(1)}% of {backfillStatus.total_target_stocks.toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Snapshots Saved
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {backfillStatus.snapshots_completed.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {backfillStatus.snapshots_progress_percent.toFixed(1)}% of {backfillStatus.total_target_snapshots.toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Estimated Time Remaining
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {backfillStatus.estimated_hours_remaining.toFixed(1)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {backfillStatus.batches_remaining} batches left
                </Typography>
              </Grid>
            </Grid>

            {/* Detailed Stats */}
            <Collapse in={showBackfillDetails}>
              <Box mt={3} pt={3} borderTop={1} borderColor="divider">
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Database Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total Unique Stocks
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {backfillStatus.database_stats.total_stocks.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fully Backfilled (23 quarters)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {backfillStatus.database_stats.fully_backfilled_stocks.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Date Range
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {backfillStatus.database_stats.earliest_date
                        ? new Date(backfillStatus.database_stats.earliest_date).toLocaleDateString()
                        : 'N/A'} - {backfillStatus.database_stats.latest_date
                        ? new Date(backfillStatus.database_stats.latest_date).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total Snapshots
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {backfillStatus.database_stats.total_snapshots.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Task Cards */}
      {tasks.map((task) => (
        <Card key={task.scheduler_name} sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">{task.scheduler_name}</Typography>
                <Chip
                  label={task.enabled ? 'Active' : 'Inactive'}
                  color={task.enabled ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            }
            subheader={task.description}
            action={
              task.scheduler_name === 'Fundamental Data Collection' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={triggeringCollection ? <CircularProgress size={16} /> : <PlayArrow />}
                  onClick={triggerFundamentalCollection}
                  disabled={triggeringCollection}
                >
                  {triggeringCollection ? 'Running...' : 'Run Now'}
                </Button>
              )
            }
            sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}
          />

          <CardContent>
            <Grid container spacing={3}>
              {/* Schedule Info */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight="bold" display="flex" alignItems="center" gap={1} mb={2}>
                  <ScheduleIcon fontSize="small" />
                  Schedule
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Frequency
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {task.schedule}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Next Run
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDateTime(task.next_run)}
                  </Typography>
                  <Typography variant="caption" color="primary">
                    {formatTimeUntil(task.next_run)}
                  </Typography>
                </Box>
              </Grid>

              {/* Last Run Results */}
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" fontWeight="bold" display="flex" alignItems="center" gap={1} mb={2}>
                  <TrendingUp fontSize="small" />
                  Last Run Results
                </Typography>
                {task.last_run ? (
                  <Grid container spacing={2}>
                    {task.last_run.timestamp && (
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Completed
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDateTime(task.last_run.timestamp)}
                        </Typography>
                      </Grid>
                    )}

                    {task.last_run.status && (
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={task.last_run.status}
                          color={task.last_run.status === 'success' ? 'success' : 'error'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                    )}

                    {/* Volatility Scanner metrics */}
                    {task.last_run.total_stocks !== undefined && (
                      <>
                        <Grid item xs={6} md={4}>
                          <Typography variant="caption" color="text.secondary">
                            Stocks Scanned
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {task.last_run.total_stocks}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="caption" color="text.secondary">
                            Successful
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {task.last_run.successful || 0}
                          </Typography>
                        </Grid>
                        {task.last_run.buy_opportunities !== undefined && (
                          <Grid item xs={6} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              BUY Opportunities
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {task.last_run.buy_opportunities}
                            </Typography>
                          </Grid>
                        )}
                        {task.last_run.sell_opportunities !== undefined && (
                          <Grid item xs={6} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              SELL Opportunities
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {task.last_run.sell_opportunities}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}

                    {/* Fundamental Collection metrics */}
                    {task.last_run.successful_fetches !== undefined && (
                      <>
                        <Grid item xs={6} md={4}>
                          <Typography variant="caption" color="text.secondary">
                            Successful Fetches
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {task.last_run.successful_fetches}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="caption" color="text.secondary">
                            Failed Fetches
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            {task.last_run.failed_fetches || 0}
                          </Typography>
                        </Grid>
                        {task.last_run.db_inserted !== undefined && (
                          <Grid item xs={6} md={4}>
                            <Typography variant="caption" color="text.secondary">
                              DB Inserted
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {task.last_run.db_inserted}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No runs completed yet
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Fundamental Collection History */}
      {collectionHistory.length > 0 && (
        <Card>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <Storage />
                <Typography variant="h6">Fundamental Collection History</Typography>
              </Box>
            }
            action={
              <IconButton onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            }
          />

          <Collapse in={showHistory}>
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Success</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell align="right">Avg Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {collectionHistory.map((record) => (
                      <TableRow key={record.snapshot_date} hover>
                        <TableCell>
                          {new Date(record.snapshot_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">{record.total_snapshots}</TableCell>
                        <TableCell align="right">
                          <Typography color="success.main" fontWeight="medium">
                            {record.successful}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error.main" fontWeight="medium">
                            {record.failed}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {record.avg_fundamental_score !== null
                            ? record.avg_fundamental_score.toFixed(1)
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Collapse>
        </Card>
      )}
    </Box>
  );
}
